/**
 * MeasurementCapture — iter150AG photo-only voltage/amps capture.
 *
 * One instance per measurement slot (e.g. "voltage running L1↔L2",
 * "amps running L1", "voltage stopped (1-phase)"). The operator:
 *   1. Taps the capture surface → device camera opens.
 *   2. Picks a photo. Component POSTs to /api/measurements (multipart)
 *      and shows the AI-extracted value while saving.
 *   3. Accepts the AI value, retakes the photo, or types a manual
 *      override (override is flagged on the record for audit).
 *
 * Per the user's design rules:
 *   - Manual typing of the value WITHOUT a photo is never possible.
 *   - The AI's confidence and rationale are surfaced so operators can
 *     spot bad reads.
 *   - Low-confidence reads (<0.7) are saved but visually flagged so
 *     QA can revisit later.
 */
import { useEffect, useRef, useState } from "react";
import {
  Camera, ArrowsClockwise, CheckCircle, Warning, CircleNotch,
  Pencil, X, ImageSquare,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const _API = process.env.REACT_APP_BACKEND_URL || "";


/** Build a `slot` key matching the backend's schema. */
function _slotKey(kind, state, phase) {
  return `${kind}:${state}:${phase}`;
}


export default function MeasurementCapture({
  parentKind,            // "commissioning" | "warranty_claim"
  parentId,              // FK to the parent record
  kind,                  // "voltage" | "amps"
  state,                 // "running" | "stopped"
  phase,                 // "l1_l2" | "l2_l3" | "l1_l3" | "l1" | "l2" | "l3" | "single"
  label,                 // human-readable label e.g. "L1 ↔ L2 (running)"
  nominalHint,           // optional float (e.g. 240) — helps the AI
  initialRecord,         // if pre-existing measurement row, pre-fill state
  onSaved,               // (record) => void — fired after every successful save
  onCleared,             // () => void — fired after delete
  required,              // bool (controls the "required" badge)
  email,                 // current user's email (for X-User-Email)
  disabled,              // bool (read-only)
}) {
  const [record, setRecord]       = useState(initialRecord || null);
  const [uploading, setUploading] = useState(false);
  const [editingOverride, setEditingOverride] = useState(false);
  const [overrideInput, setOverrideInput]     = useState("");
  const [overrideReason, setOverrideReason]   = useState("");
  const inputRef = useRef(null);

  useEffect(() => { setRecord(initialRecord || null); }, [initialRecord]);

  const unit       = kind === "voltage" ? "V" : "A";
  const slotKey    = _slotKey(kind, state, phase);
  const hasPhoto   = !!record;
  const finalValue = record?.final_value;
  const aiValue    = record?.ai_value;
  const conf       = record?.ai_confidence;
  const isOverride = !!record?.manual_override;
  const isLowConf  = !!record?.low_confidence;
  const photoUrl   = record?.id
    ? `${_API}/api/measurements/${record.id}/photo`
    : null;

  async function uploadPhoto(file) {
    if (!file) return;
    if (disabled) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("parent_kind", parentKind);
      fd.append("parent_id",   parentId);
      fd.append("kind",        kind);
      fd.append("state",       state);
      fd.append("phase",       phase);
      if (nominalHint) fd.append("nominal_hint", String(nominalHint));
      fd.append("file", file);
      const resp = await fetch(`${_API}/api/measurements`, {
        method: "POST",
        headers: { "X-User-Email": email || "" },
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data?.detail || `Upload failed (${resp.status})`);
        return;
      }
      setRecord(data);
      onSaved?.(data);
      if (data.ai_value == null) {
        toast.warning(`AI couldn't read this ${kind} photo. Retake or use manual override.`);
      } else if (data.low_confidence) {
        toast.warning(`AI extracted ${data.final_value} ${unit} but confidence is low. Verify or retake.`);
      } else {
        toast.success(`AI read ${data.final_value} ${unit}`);
      }
    } catch (e) {
      toast.error(e?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function submitOverride() {
    const v = parseFloat(overrideInput);
    if (!isFinite(v)) {
      toast.error("Enter a numeric value");
      return;
    }
    if (!record?.id) {
      toast.error("Take a photo first — manual override requires the photo for audit");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("manual_value", String(v));
      if (overrideReason.trim()) fd.append("override_reason", overrideReason.trim());
      const resp = await fetch(`${_API}/api/measurements/${record.id}`, {
        method: "PATCH",
        headers: { "X-User-Email": email || "" },
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data?.detail || "Override failed");
        return;
      }
      setRecord(data);
      onSaved?.(data);
      setEditingOverride(false);
      toast.success(`Manual override: ${v} ${unit}`);
    } catch (e) {
      toast.error(e?.message || "Override failed");
    }
  }

  async function clearOverride() {
    if (!record?.id) return;
    try {
      const fd = new FormData();
      fd.append("clear_override", "true");
      const resp = await fetch(`${_API}/api/measurements/${record.id}`, {
        method: "PATCH",
        headers: { "X-User-Email": email || "" },
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data?.detail || "Clear failed");
        return;
      }
      setRecord(data);
      onSaved?.(data);
      toast.success("Reverted to AI reading");
    } catch (e) {
      toast.error(e?.message || "Clear failed");
    }
  }

  async function remove() {
    if (!record?.id) return;
    if (!window.confirm(`Delete this ${label || kind} photo?`)) return;
    try {
      const resp = await fetch(`${_API}/api/measurements/${record.id}`, {
        method: "DELETE",
        headers: { "X-User-Email": email || "" },
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        toast.error(data?.detail || "Delete failed");
        return;
      }
      setRecord(null);
      onCleared?.();
      toast.success("Measurement cleared");
    } catch (e) {
      toast.error(e?.message || "Delete failed");
    }
  }

  return (
    <div data-testid={`meas-${slotKey}`}
         className={`bg-white border rounded-sm p-3 ${
           hasPhoto && !isLowConf && !isOverride ? "border-emerald-300"
             : hasPhoto && isOverride ? "border-amber-300"
             : hasPhoto && isLowConf  ? "border-rose-300"
             : "border-slate-200"}`}>
      <div className="flex items-start gap-3">
        {/* Photo thumb */}
        <button type="button"
                data-testid={`meas-${slotKey}-capture`}
                disabled={disabled || uploading}
                onClick={() => inputRef.current?.click()}
                className="shrink-0 w-20 h-20 border border-dashed border-slate-300 rounded-sm bg-slate-50 flex items-center justify-center hover:bg-slate-100 overflow-hidden">
          {uploading ? (
            <CircleNotch size={22} className="animate-spin text-slate-400" />
          ) : photoUrl ? (
            <img src={photoUrl} alt={label || slotKey}
                  className="w-full h-full object-cover" />
          ) : (
            <Camera size={22} className="text-slate-400" />
          )}
        </button>
        <input ref={inputRef} type="file" accept="image/*"
                capture="environment"
                className="hidden"
                onChange={e => uploadPhoto(e.target.files?.[0])}
                data-testid={`meas-${slotKey}-file`} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-slate-800">{label || slotKey}</span>
            {required && !hasPhoto && (
              <span className="text-[9px] uppercase tracking-wider bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-sm font-bold">
                required
              </span>
            )}
          </div>

          {/* Value display */}
          {hasPhoto ? (
            <div className="mt-1">
              <div className="text-base font-bold font-mono text-slate-900 tabular-nums"
                   data-testid={`meas-${slotKey}-value`}>
                {finalValue != null ? `${finalValue} ${unit}` : "—"}
              </div>
              <div className="text-[10px] mt-0.5 flex items-center gap-1 flex-wrap">
                {isOverride ? (
                  <span className="inline-flex items-center gap-1 text-amber-700">
                    <Pencil size={9} weight="fill" /> manual override
                    {aiValue != null && <span className="text-slate-500">(AI saw {aiValue} {unit})</span>}
                  </span>
                ) : aiValue != null ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    <CheckCircle size={9} weight="fill" /> AI ·
                    <span className="text-slate-500">{(conf * 100).toFixed(0)}% confidence</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-rose-700">
                    <Warning size={9} weight="fill" /> AI couldn't read
                  </span>
                )}
                {isLowConf && (
                  <span className="inline-flex items-center gap-1 text-amber-700 ml-1">
                    <Warning size={9} weight="fill" /> low confidence
                  </span>
                )}
              </div>
              {record?.ai_notes && (
                <div className="text-[10px] text-slate-500 mt-0.5 italic">
                  {record.ai_notes}
                </div>
              )}
            </div>
          ) : (
            <div className="text-[11px] text-slate-500 mt-1">
              Tap the camera icon — point at the meter LCD and snap a clear photo.
            </div>
          )}

          {/* Action buttons */}
          {hasPhoto && !disabled && (
            <div className="mt-2 flex items-center gap-1 flex-wrap">
              <button type="button"
                      data-testid={`meas-${slotKey}-retake`}
                      onClick={() => inputRef.current?.click()}
                      className="text-[10px] inline-flex items-center gap-1 px-1.5 py-1 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
                <ArrowsClockwise size={10} /> Retake
              </button>
              {!isOverride ? (
                <button type="button"
                        data-testid={`meas-${slotKey}-override`}
                        onClick={() => {
                          setOverrideInput(aiValue != null ? String(aiValue) : "");
                          setOverrideReason("");
                          setEditingOverride(true);
                        }}
                        className="text-[10px] inline-flex items-center gap-1 px-1.5 py-1 rounded-sm border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100">
                  <Pencil size={10} /> Override
                </button>
              ) : (
                <button type="button"
                        data-testid={`meas-${slotKey}-revert`}
                        onClick={clearOverride}
                        className="text-[10px] inline-flex items-center gap-1 px-1.5 py-1 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
                  <ArrowsClockwise size={10} /> Use AI reading
                </button>
              )}
              <button type="button"
                      data-testid={`meas-${slotKey}-delete`}
                      onClick={remove}
                      className="text-[10px] inline-flex items-center gap-1 px-1.5 py-1 rounded-sm border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 ml-auto">
                <X size={10} /> Delete
              </button>
            </div>
          )}

          {/* Override editor */}
          {editingOverride && (
            <div data-testid={`meas-${slotKey}-override-editor`}
                 className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-sm space-y-1.5">
              <p className="text-[10px] text-amber-800 leading-snug">
                <Warning size={9} weight="fill" className="inline -mt-0.5 mr-0.5" />
                Manual overrides are flagged on the record. Use only when the
                meter is hard to read or AI extraction is wrong.
              </p>
              <div className="flex items-center gap-1">
                <input type="number" step="0.1" inputMode="decimal"
                        data-testid={`meas-${slotKey}-override-input`}
                        value={overrideInput}
                        onChange={e => setOverrideInput(e.target.value)}
                        placeholder={`Reading in ${unit}`}
                        className="text-xs px-2 py-1 border border-amber-300 rounded-sm font-mono flex-1" />
                <span className="text-[11px] text-slate-600 font-mono">{unit}</span>
              </div>
              <input type="text" maxLength={120}
                      data-testid={`meas-${slotKey}-override-reason`}
                      value={overrideReason}
                      onChange={e => setOverrideReason(e.target.value)}
                      placeholder="Reason (e.g. glare on display)"
                      className="text-[11px] px-2 py-1 border border-amber-300 rounded-sm w-full" />
              <div className="flex items-center gap-1">
                <button type="button"
                        data-testid={`meas-${slotKey}-override-cancel`}
                        onClick={() => setEditingOverride(false)}
                        className="text-[10px] px-2 py-1 rounded-sm border border-slate-300 bg-white text-slate-600 hover:bg-slate-50">
                  Cancel
                </button>
                <button type="button"
                        data-testid={`meas-${slotKey}-override-save`}
                        onClick={submitOverride}
                        className="ml-auto text-[10px] px-2 py-1 rounded-sm border border-amber-600 bg-amber-600 text-white hover:bg-amber-700">
                  Save override
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// MeasurementsSet — renders all required voltage + amps slots for one
// commissioning OR warranty-claim record, based on phaseCount (1 | 3).
// Handles the per-flow rules (warranty amps optional, etc.).
// ─────────────────────────────────────────────────────────────────────
export function MeasurementsSet({
  parentKind, parentId, phaseCount, nominalVoltage,
  email, ampsRequired = true, disabled = false,
  onChange,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    try {
      const r = await fetch(`${_API}/api/measurements?parent_kind=${encodeURIComponent(parentKind)}&parent_id=${encodeURIComponent(parentId)}`, {
        headers: { "X-User-Email": email || "" },
      });
      const d = await r.json();
      setItems(d.items || []);
      onChange?.(d.items || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [parentKind, parentId]);

  function recordFor(slotKey) {
    return items.find(i => i.slot === slotKey) || null;
  }

  function handleSaved(rec) {
    setItems(prev => {
      const next = prev.filter(i => i.slot !== rec.slot);
      next.push(rec);
      onChange?.(next);
      return next;
    });
  }
  function handleCleared(slotKey) {
    setItems(prev => {
      const next = prev.filter(i => i.slot !== slotKey);
      onChange?.(next);
      return next;
    });
  }

  const VOLT_SLOTS = phaseCount === 1
    ? [
        { kind: "voltage", state: "running", phase: "single",
          label: "Voltage — running" },
        { kind: "voltage", state: "stopped", phase: "single",
          label: "Voltage — stopped" },
      ]
    : [
        { kind: "voltage", state: "running", phase: "l1_l2",
          label: "L1 ↔ L2 — running" },
        { kind: "voltage", state: "running", phase: "l2_l3",
          label: "L2 ↔ L3 — running" },
        { kind: "voltage", state: "running", phase: "l1_l3",
          label: "L1 ↔ L3 — running" },
        { kind: "voltage", state: "stopped", phase: "l1_l2",
          label: "L1 ↔ L2 — stopped" },
        { kind: "voltage", state: "stopped", phase: "l2_l3",
          label: "L2 ↔ L3 — stopped" },
        { kind: "voltage", state: "stopped", phase: "l1_l3",
          label: "L1 ↔ L3 — stopped" },
      ];

  const AMP_SLOTS = phaseCount === 1
    ? [
        { kind: "amps", state: "running", phase: "single",
          label: "Amps — running" },
      ]
    : [
        { kind: "amps", state: "running", phase: "l1", label: "Amps L1 — running" },
        { kind: "amps", state: "running", phase: "l2", label: "Amps L2 — running" },
        { kind: "amps", state: "running", phase: "l3", label: "Amps L3 — running" },
      ];

  return (
    <div className="space-y-3" data-testid="measurements-set">
      <section>
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1.5">
          Voltage <span className="text-rose-600 ml-0.5">*</span>
          <span className="ml-2 text-[10px] text-slate-400 normal-case font-normal">
            (photo-only, AI reads the LCD)
          </span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {VOLT_SLOTS.map(s => (
            <MeasurementCapture key={s.kind + s.state + s.phase}
              parentKind={parentKind} parentId={parentId}
              kind={s.kind} state={s.state} phase={s.phase}
              label={s.label}
              nominalHint={nominalVoltage}
              initialRecord={recordFor(_slotKey(s.kind, s.state, s.phase))}
              onSaved={handleSaved}
              onCleared={() => handleCleared(_slotKey(s.kind, s.state, s.phase))}
              required
              email={email}
              disabled={disabled} />
          ))}
        </div>
      </section>

      <section>
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1.5">
          Amps {ampsRequired && <span className="text-rose-600 ml-0.5">*</span>}
          <span className="ml-2 text-[10px] text-slate-400 normal-case font-normal">
            (running only{!ampsRequired ? " — optional, only if the compressor runs" : ""})
          </span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {AMP_SLOTS.map(s => (
            <MeasurementCapture key={s.kind + s.state + s.phase}
              parentKind={parentKind} parentId={parentId}
              kind={s.kind} state={s.state} phase={s.phase}
              label={s.label}
              initialRecord={recordFor(_slotKey(s.kind, s.state, s.phase))}
              onSaved={handleSaved}
              onCleared={() => handleCleared(_slotKey(s.kind, s.state, s.phase))}
              required={ampsRequired}
              email={email}
              disabled={disabled} />
          ))}
        </div>
      </section>

      {loading && (
        <div className="text-[11px] text-slate-400 flex items-center gap-1">
          <CircleNotch size={11} className="animate-spin" /> Loading saved measurements…
        </div>
      )}
    </div>
  );
}


/** Helper used by the parent flow to compute whether the required
 *  slots are all populated (for "Continue" gating). */
export function isMeasurementsSetComplete(items, phaseCount, ampsRequired = true) {
  if (!Array.isArray(items)) return false;
  const haveSlot = (k, s, p) =>
    items.some(i => i.slot === _slotKey(k, s, p) && i.final_value != null);
  if (phaseCount === 1) {
    if (!haveSlot("voltage", "running", "single")) return false;
    if (!haveSlot("voltage", "stopped", "single")) return false;
    if (ampsRequired && !haveSlot("amps", "running", "single")) return false;
    return true;
  }
  for (const ph of ["l1_l2", "l2_l3", "l1_l3"]) {
    if (!haveSlot("voltage", "running", ph)) return false;
    if (!haveSlot("voltage", "stopped", ph)) return false;
  }
  if (ampsRequired) {
    for (const ph of ["l1", "l2", "l3"]) {
      if (!haveSlot("amps", "running", ph)) return false;
    }
  }
  return true;
}
